const PUBLIC_AUTH_PATHS = new Set([
  '/',
  '/login',
  '/login-failed',
  '/login-success',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/subscribe',
]);

export const isPublicAuthPath = (pathname: string): boolean =>
  PUBLIC_AUTH_PATHS.has(pathname);

const isCourseTrainPath = (pathname: string): boolean =>
  pathname === '/courses/train' ||
  pathname.endsWith('/train') ||
  /\/train\//.test(pathname);

/** Interactive lesson trainers — need a guest or real session to persist. */
const isCourseLessonPath = (pathname: string): boolean =>
  /\/lessons\//.test(pathname);

/** Per-user mistakes decks — not SEO catalog. */
const isMistakesCoursePath = (pathname: string): boolean =>
  pathname === '/courses/mistakes' || pathname.startsWith('/courses/mistakes/');

/**
 * SEO / catalog surfaces that work without a session — including returning
 * signed-out users who still have the prior-login cookie.
 * Course train/lesson/mistakes routes still require a session.
 */
export const isAnonymousReadablePath = (pathname: string): boolean => {
  if (isPublicAuthPath(pathname) || pathname === '/contact') {
    return true;
  }
  if (pathname === '/games/browse' || pathname.startsWith('/games/browse/')) {
    return true;
  }
  if (pathname === '/explorer' || pathname.startsWith('/explorer/')) {
    return true;
  }
  if (pathname === '/courses' || pathname.startsWith('/courses/')) {
    return (
      !isCourseTrainPath(pathname) &&
      !isCourseLessonPath(pathname) &&
      !isMistakesCoursePath(pathname)
    );
  }
  return false;
};

export const shouldRedirectToLogin = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return !isAnonymousReadablePath(window.location.pathname);
};

export const rememberAuthReturnTo = (returnTo: string): void => {
  if (typeof window === 'undefined') {
    return;
  }
  if (returnTo && returnTo !== '/') {
    sessionStorage.setItem('authReturnTo', returnTo);
  }
};

export const consumeAuthReturnTo = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const returnTo = sessionStorage.getItem('authReturnTo');
  if (returnTo) {
    sessionStorage.removeItem('authReturnTo');
  }
  return returnTo;
};

/** Resolve post-login destination from legacy ?from= links or session storage. */
export const resolveAuthReturnTo = (fromQuery?: string | null): string | null => {
  if (fromQuery && fromQuery !== '/') {
    return fromQuery;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  const stored = sessionStorage.getItem('authReturnTo');
  return stored && stored !== '/' ? stored : null;
};

export const buildLoginPath = (returnTo?: string): string => {
  if (returnTo && returnTo !== '/') {
    rememberAuthReturnTo(returnTo);
  }
  return '/login';
};

export const redirectToLogin = (): void => {
  if (!shouldRedirectToLogin()) {
    return;
  }

  const returnTo = `${window.location.pathname}${window.location.search}`;
  rememberAuthReturnTo(returnTo);
  window.location.assign(buildLoginPath(returnTo));
};
